"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _APICommands = require("../constants/APICommands");
var _ServiceRegistry = _interopRequireDefault(require("../util/ServiceRegistry"));
var _ApiConstants = _interopRequireDefault(require("../constants/ApiConstants"));
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); } /*************************************************************************
* ADOBE CONFIDENTIAL
* ___________________
*
* Copyright 2020 Adobe
* All Rights Reserved.
*
* NOTICE: All information contained herein is, and remains
* the property of Adobe and its suppliers, if any. The intellectual
* and technical concepts contained herein are proprietary to Adobe
* and its suppliers and are protected by all applicable intellectual
* property laws, including trade secret and copyright laws.
* Dissemination of this information or reproduction of this material
* is strictly forbidden unless prior written permission is obtained
* from Adobe.
**************************************************************************/
class CommandManager {
  constructor(serviceFactory, isViewSDKPublic = false) {
    this._serviceFactory = serviceFactory;
    this._isViewSDKPublic = isViewSDKPublic;
  }

  /**
   * @method
   * @description
   * executeCommand(command, commandData) method to execute commands on file preview.
   *
   * @param {String} command - Command which we wanted to execute on file preview
   * @param {Object} commandData - data which should be passed with command.
   * @param options
   *
   * @return {Promise} - promise that resolves with result of command.
   */
  executeCommand(command, commandData = {}, options = {}) {
    if (_APICommands.AppAPICommands[command] || _APICommands.PreviewAPICommands[command]) {
      return this._serviceFactory.getService("CommandReceiverService").executeCommand({
        command,
        commandData,
        options
      });
    }
    return Promise.reject({
      message: `Command ${command} not supported.`
    });
  }
  switchPreview(opts) {
    return this.executeCommand(_APICommands.PreviewAPICommands.SWITCH_PREVIEW, opts);
  }

  /**
   * @method
   * @description
   * returns basic information about the PDF fetched from the PDF dictionary, such as the number
   * of pages and title of the PDF
   *
   * @return {Promise} - promise that resolves with pdf metadata.
   */
  getPDFMetadata() {
    return this.executeCommand(_APICommands.PreviewAPICommands.GET_PDF_METADATA);
  }

  /**
   * @method
   * @description
   * sets the page cursor to any cursor style
   */
  setCursor(cursor) {
    return this.executeCommand(_APICommands.PreviewAPICommands.SET_CURSOR, cursor);
  }

  /**
   * @method
   * @description
   * revokes an applied cursor style and reverts it to the default cursor style.
   */
  resetCursor() {
    return this.executeCommand(_APICommands.PreviewAPICommands.RESET_CURSOR);
  }

  /**
   * @method
   * @description
   * returns the current page number of the in focus page
   *
   * @return {Promise} - promise that resolves with current page number.
   */
  getCurrentPage() {
    return this.executeCommand(_APICommands.PreviewAPICommands.GET_CURRENT_PAGE);
  }

  /**
   * @method
   * @description
   * takes the PDF page number as input and returns the zoom level of that page.
   *
   * @return {Promise} - promise that resolves with current zoom level of pdf.
   */
  getPageZoom(pageNumber) {
    return this.executeCommand(_APICommands.PreviewAPICommands.GET_PAGE_ZOOM_LEVEL, pageNumber);
  }
  setDarkPageThemeSwitchDisabled(darkPageThemeSwitch) {
    return this.executeCommand(_APICommands.PreviewAPICommands.SET_DARK_PAGE_THEME_SWITCH_DISABLED, darkPageThemeSwitch);
  }
  setDocumentViewThemeChange(themeData) {
    return this.executeCommand(_APICommands.PreviewAPICommands.SET_DOCUMENT_VIEW_THEME_CHANGE, themeData);
  }

  /**
   * @method
   * @description
   * takes the PDF page number as input and returns the page attributes (height, width etc) of that page.
   *
   * @return {Promise} - promise that resolves with page attributes.
   */
  getPageAttributes(pageNumber) {
    return this.executeCommand(_APICommands.PreviewAPICommands.GET_PAGE_ATTRIBUTES, pageNumber);
  }

  /**
   * It accepts a page number as input and returns the text and text attributes for that page
   * @param {Number} pageNumber - page number to extract data from
   * @return {Promise} - promise that resolves with extracted page text metadata
   */
  extractPageText(pageNumber) {
    return this.executeCommand(_APICommands.PreviewAPICommands.EXTRACT_PAGE_TEXT, pageNumber);
  }

  /**
   * @method
   * @description
   * This API enables navigation to any PDF page. It accepts a page number as input. You can also pass the x
   * and y coordinates on the page as optional input parameters to enable navigation to a particular location
   * on the page. When no coordinates are passed, the default coordinates are (0, 0)
   *
   * @param {Number} pageNumber
   * @param {Number} left
   * @param {Number} top
   * @param {Object} scrollOptions - The scroll options for the navigation.
   * @param {number} scrollOptions.percScrollOffsetY - The percentage scroll offset on the y-axis.
   * @param {number} scrollOptions.percScrollOffsetX - The percentage scroll offset on the x-axis.
   * @param {boolean} scrollOptions.noScrollIfVisible - Whether to scroll if the location is already visible.
   * @param {number} scrollOptions.maxScrollOffsetY - The maximum scroll offset on the y-axis
   */
  gotoLocation(pageNumber, left, top, scrollOptions = {}) {
    return this.executeCommand(_APICommands.PreviewAPICommands.GOTO_LOCATION, {
      pageNumber,
      left: left || 0,
      top: top || 0,
      scrollOptions
    });
  }
  addBottomSpaceBelowDocumentInMobile(space, opts = {}) {
    return this.executeCommand(_APICommands.PreviewAPICommands.ADD_SPACE_BELOW_DOCUMENT_IN_MOBILE, {
      space,
      opts
    });
  }

  /**
   * @method
   * @description
   * accepts a page number as input and clears any text selection applied to the page.
   *
   * @param {Number} pageNumber
   */
  clearPageSelection(pageNumber) {
    return this.executeCommand(_APICommands.PreviewAPICommands.CLEAR_PAGE_SELECTION, pageNumber);
  }

  /**
   * Get selected text for the provided PDF points
   * @param {object} params.startPoint - { pageNum, x, y }
   * @param {object} params.endPoint - { pageNum, x, y }
   * @param {boolean} params.richText - true if rich text is required, default false
   * @return {Promise} - promise that resolves with selected text
   */
  getTextBetweenPDFPoints(params) {
    return this.executeCommand(_APICommands.PreviewAPICommands.GET_TEXT_BETWEEN_PDF_POINTS, params);
  }

  /**
   * @method
   * @description
   * enable or disable the text selection features
   *
   * @param {Boolean} enable text selection enabled if param is true
   */
  enableTextSelection(enable) {
    return this.executeCommand(_APICommands.PreviewAPICommands.TOGGLE_TEXT_SELECTION, enable);
  }

  /**
   * @method
   * @description
   * enable or disable interactive/builtin tooltip
   *
   * @param {Boolean} enable interactive tooltip enabled if param is true
   */
  enableInteractiveTooltip(enable) {
    return this.executeCommand(_APICommands.PreviewAPICommands.ENABLE_INTERACTIVE_TOOLTIP, enable);
  }

  /**
   * @method
   * @description
   * checks the PDF for XMP metadata and returns it if present
   *
   * @return {Promise} - promise that resolves with xmp metadata of documents.
   */
  getXMPMetadata(fields = [], rawStream = false) {
    return this.executeCommand(_APICommands.PreviewAPICommands.GET_XMP_METADATA, {
      fields,
      rawStream
    });
  }

  /**
   * @method
   * @description
   * If a user selects any content in the viewer, then the selected content can be fetched using this API.
   */
  getSelectedContent() {
    return this.executeCommand(_APICommands.PreviewAPICommands.GET_SELECTED_CONTENT);
  }
  getTextFromDocRects(startPoint, endPoint) {
    return this.executeCommand(_APICommands.PreviewAPICommands.GET_TEXT_FROM_DOC_RECTS, startPoint, endPoint);
  }

  /**
   * @method
   * @description
   * It takes the search text as input and highlights the first search result in UI from the viewing page
   *
   * @param searchText string to be searched
   *
   * @return {Promise} - promise that resolves with search object.
   */
  search(searchText) {
    return this.executeCommand(_APICommands.PreviewAPICommands.START_SEARCH, {
      searchText
    });
  }
  performSearch(text, startPage, numPages, priority, zoomLevel) {
    return this.executeCommand(_APICommands.PreviewAPICommands.PERFORM_SEARCH, {
      text,
      startPage,
      numPages,
      priority,
      zoomLevel
    });
  }
  getBookmarkAPIs() {
    return {
      /**
       * @method
       * @description
       * This API is used to get all the bookmarks residing in the PDF file.
       *
       * @return {Promise} - promise that resolves with array of Bookmark Object
      */
      getBookmarks: () => this.executeCommand(_APICommands.PreviewAPICommands.GET_ALL_BOOKMARK),
      /**
       * @method
       * @description
       * This API is used to open the bookmark residing in the PDF file.
       *
       * @param id bookmarkId
       *
       * @return {Promise} - promise that resolves with boolean
      */
      openBookmark: id => this.executeCommand(_APICommands.PreviewAPICommands.OPEN_BOOKMARK, id)
    };
  }
  getAttachmentAPIs() {
    return {
      /**
       * @method
       * @description
       * This API is used to get all the attachments residing in the PDF file.
       *
       * @return {Promise} - promise that resolves with array of Attachment Objects.
      */
      getAttachments: () => this.executeCommand(_APICommands.PreviewAPICommands.GET_ATTACHMENTS),
      /**
       * @method
       * @description
       * This API is used to get the attachment residing in the PDF file.
       *
       * @param name fileName
       *
       * @return {Promise} - promise that resolves with Attachment Buffer Object.
       */
      getAttachmentBuffer: name => this.executeCommand(_APICommands.PreviewAPICommands.GET_ATTACHMENT_BUFFER, name)
    };
  }
  getZoomAPIs() {
    return {
      /**
       * @method
       * @description
       * the document zooms in to the given zoom level such that it lies between minZoom and maxZoom
       *
       * @param level zoomLevel
       *
       * @return {Promise} - promise that resolves with current zoom level
       */
      setZoomLevel: level => this.executeCommand(_APICommands.PreviewAPICommands.SET_ZOOM_LEVEL, level),
      /**
       * @method
       * @description
       * the document zooms in to the next zoom level such that it's never greater than maxZoom
       *
       * @return {Promise} - promise that resolves with current zoom level
       */
      zoomIn: () => this.executeCommand(_APICommands.PreviewAPICommands.ZOOM_IN),
      /**
       * @method
       * @description
       * the document zooms out to the previous zoom level such that it's never less than minZoom
       *
       * @return {Promise} - promise that resolves with current zoom level
       */
      zoomOut: () => this.executeCommand(_APICommands.PreviewAPICommands.ZOOM_OUT),
      /**
       * @method
       * @description
       * returns the minimum and maximum allowed zoom levels of a document.
       *
       * @return {Promise} - promise that resolves with min and max zoom level
       */
      getZoomLimits: () => this.executeCommand(_APICommands.PreviewAPICommands.GET_ZOOM_LIMIT)
    };
  }
  getReadAloudAPIs() {
    return {
      /**
       * @method
       * @description
       * enters read aloud mode and starts reading pdf content
       *
       * @return {Promise} - a promise, on resolution enters read aloud mode and starts reading.
       * Will also send read aloud controls to topbar using customiseTopBar event.
       */
      start: () => this.executeCommand(_APICommands.PreviewAPICommands.START_READ_ALOUD),
      /**
       * @method
       * @description
       * stops reading (if read aloud is active) and exits read aloud mode.
       */
      stop: () => this.executeCommand(_APICommands.PreviewAPICommands.STOP_READ_ALOUD),
      /**
       * @method
       * @description
       * check whether read aloud is paused or not.
       *
       * @return {Promise} - promise that resolves with true if read aloud
       * is paused and false if read loud is not paused
       */
      isPaused: () => this.executeCommand(_APICommands.PreviewAPICommands.IS_READ_ALOUD_PAUSED),
      /**
       * @method
       * @description
       * pause read aloud
       *
       * @return {Promise} - promise that resolves with true if successfully paused and
       * false if pause() called when isPaused() is true (already paused)
       */
      pause: () => this.executeCommand(_APICommands.PreviewAPICommands.PAUSE_READ_ALOUD),
      /**
       * @method
       * @description
       * resume read aloud
       *
       * @return {Promise} - promise that resolves with true if successfully resumed and
       * false if play() called when isPaused() is false (already playing)
       */
      play: () => this.executeCommand(_APICommands.PreviewAPICommands.PLAY_READ_ALOUD),
      /**
       * @method
       * @description
       * check whether there is any next paragraph to read
       *
       * @return {Promise} - promise that resolves with true if next() can be called, ie read aloud is not paused
       * and next paragraph is available and false if next() should not be called, ie read aloud is paused
       * and/or next paragraph is not available
       */
      hasNext: () => this.executeCommand(_APICommands.PreviewAPICommands.HAS_NEXT_PARAGRAPH),
      /**
       * @method
       * @description
       * start reading next paragraph (if hasNext() is true)
       *
       * @return {Promise} - promise that resolves with true if operation is successful
       * false if called when hasNext() is false
       */
      next: () => this.executeCommand(_APICommands.PreviewAPICommands.GO_TO_NEXT_PARAGRAPH),
      /**
       * @method
       * @description
       * check whether there is any previous paragraph to read
       *
       * @return {Promise} - promise that resolves with true if prev() can be called, ie read aloud
       * is not paused and previous paragraph is available, and false if prev() should not be called,
       * ie read aloud is paused and/or previous paragraph is not available
       */
      hasPrev: () => this.executeCommand(_APICommands.PreviewAPICommands.HAS_PREVIOUS_PARAGRAPH),
      /**
       * @method
       * @description
       * start reading previous paragraph (if hasPrev() is true)
       *
       * @return {Promise} - promise that resolves with true if operation is successful, and
       * false if called when hasPrev() is false
       */
      prev: () => this.executeCommand(_APICommands.PreviewAPICommands.GO_TO_PREVIOUS_PARAGRAPH),
      /**
       * @method
       * @description
       * provides all the available voice rates
       *
       * @return {Promise} - promise that resolves with array of available voice rates that the user can choose
       */
      getVoiceRates: () => this.executeCommand(_APICommands.PreviewAPICommands.GET_VOICE_RATES),
      /**
       * @method
       * @description
       * set the voice rate to the value at provided index of array
       *
       * @param index arrayIndex
       *
       * @return {Promise} - promise that resolves with true if operation is successful, and
       * false if called with index out of bounds
       */
      setVoiceRate: index => this.executeCommand(_APICommands.PreviewAPICommands.SET_VOICE_RATE, index),
      /**
       * @method
       * @description
       * provide the index of currently selected voice rate
       *
       * @return {Promise} - promise that resolves with index of array (0-based) returned
       * by getVoiceRates() that is currently selected
       */
      getSelectedVoiceRate: () => this.executeCommand(_APICommands.PreviewAPICommands.GET_SELECTED_VOICE_RATE),
      /**
       * @method
       * @description
       * provides all the available voice options
       *
       * @return {Promise} - promise that resolves with array of available voice options objects (speakers)
       * that the user can choose.
       */
      getVoiceOptions: () => this.executeCommand(_APICommands.PreviewAPICommands.GET_VOICE_OPTIONS),
      /**
       * @method
       * @description
       * set the voice option to the value at provided index of array
       *
       * @param index arrayIndex
       *
       * @return {Promise} - promise that resolves with true if operation is successful and
       * false if called with index out of bounds
       */
      setVoiceOption: index => this.executeCommand(_APICommands.PreviewAPICommands.SET_VOICE_OPTION, index),
      /**
       * @method
       * @description
       * provide the index of currently selected voice option
       *
       * @return {Promise} - promise that resolves with index of array (0-based) returned by
       * getVoiceOptions() that is currently selected
       */
      getSelectedVoiceOption: () => this.executeCommand(_APICommands.PreviewAPICommands.GET_SELECTED_VOICE_OPTION)
    };
  }
  getCaretModeApis() {
    return {
      /**
       * @method
       * @description
       * start Caret Mode
       *
       * @return {Promise} - promise that resolves with true if operation is successful
       */
      start: () => this.executeCommand(_APICommands.PreviewAPICommands.START_CARET_MODE),
      /**
       * @method
       * @description
       * stop Caret Mode
       *
       * @return {Promise} - promise that resolves with true if operation is successful
       */
      stop: () => this.executeCommand(_APICommands.PreviewAPICommands.STOP_CARET_MODE)
    };
  }

  /**
   * @method
   * @description
   * get the search UI component
   */
  getSearchUI() {
    return this.executeCommand(_APICommands.PreviewAPICommands.GET_SEARCH_UI);
  }

  /**
   * @method
   * @description register search addon panel
   * @param {Function} callback - callback function that resolves to a component
   * @param {String} trigger - trigger for search addon panel (ON_FOCUS or ALWAYS)
   * @return {Boolean} true if successfully registered, false otherwise
   */
  registerSearchAddOnPanel({
    callback,
    getTripleDotMenuItems,
    trigger = _ApiConstants.default.SEARCH_ADDON_TRIGGER.ON_FOCUS
  }) {
    return this.executeCommand(_APICommands.PreviewAPICommands.REGISTER_SEARCH_ADDON_PANEL, {
      callback,
      getTripleDotMenuItems,
      trigger
    });
  }

  /**
   * @method
   * @description Get the bounding box of the page
   * @param {Number} pageIndex - page index
   * @return {Promise} - promise that resolves with bounding box of the page
   */
  getBBoxForPage({
    pageIndex
  }) {
    return this.executeCommand(_APICommands.PreviewAPICommands.GET_BBOX_FOR_PAGE, {
      pageIndex
    });
  }

  /**
   * @method
   * @description Get a bounding box content of the page
   * @param {Number} pageIndex - page index
   * @param {Number} itemIndex - item index in the page
   * @param {Object} bboxBounds - bounding box bounds
   * @return {Promise} - promise that resolves with bounding box of the page
   */
  getBBoxContent({
    pageIndex,
    itemIndex,
    bboxBounds
  }) {
    return this.executeCommand(_APICommands.PreviewAPICommands.GET_BBOX_CONTENT, {
      pageIndex,
      itemIndex,
      bboxBounds
    });
  }

  /**
    * @method
    * @description modify accessibility tree through this callback
    * @param {Function} callback - callback function that modifies current accessibility tree structure
    * @return {Object} New modified accessibility tree
    */
  registerBeforeA11yTreeRenderCallback(callback) {
    return this.executeCommand(_APICommands.PreviewAPICommands.REGISTER_BEFORE_A11Y_TREE_RENDER_CALLBACK, {
      callback
    });
  }

  /**
   * @method
   * @description register sticky tools addon panel
   * @param {Function} callback - callback function that resolves to a component
   * @return {Boolean} true if successfully registered, false otherwise
   */
  registerStickyToolsAddOnPanel(callback) {
    return this.executeCommand(_APICommands.PreviewAPICommands.REGISTER_STICKY_TOOLS_ADDON_PANEL, callback);
  }

  /**
   * @method
   * @description get the document id
   * @param None
   * @return {String} documentId
   */
  getDocumentId() {
    return this.executeCommand(_APICommands.PreviewAPICommands.GET_DOCUMENT_ID);
  }

  /**
   * @method
   * @description get to know when initial page is loaded
   * @param None
   * @return {Promise} which resolves when initial page is loadeda and rejects with error code if any error occurs
   */
  getInitialPageLoaded() {
    return this.executeCommand(_APICommands.PreviewAPICommands.INITIAL_PAGE_LOADED);
  }
  closeViewer() {
    return this.executeCommand(_APICommands.AppAPICommands.CLOSE_PDF_VIEWER);
  }
  unMountViewer() {
    return _ServiceRegistry.default.getService("EmbedModeService").unMountApp();
  }

  /**
   * @method
   * @description
   * returns the thumbnail of the specified page in a document
   *
   * @param pageNumbers - array of page numbers
   * @param options - options can be zoomLevel
   *
   * @return {Promise} - promise that resolves with thumbnail data for page numbers
   */
  getThumbnail(pageNumbers, options = {}) {
    return Promise.all([this.getZoomAPIs().getZoomLimits(), this.getPDFMetadata()]).then(([zoomData, {
      numPages: totalPageCount
    }]) => this.executeCommand(_APICommands.PreviewAPICommands.GET_THUMBNAIL, {
      pageNumbers,
      options
    }, {
      preCalculatedData: {
        zoomData,
        totalPageCount
      }
    }));
  }
  savePDF() {
    return this.executeCommand(_APICommands.PreviewAPICommands.SAVE_PDF);
  }
  applyCNPDFUpdates(commandData) {
    return this.executeCommand(_APICommands.PreviewAPICommands.APPLY_CNPDF_UPDATES, commandData);
  }
  downloadPDF() {
    return this.executeCommand(_APICommands.PreviewAPICommands.DOWNLOAD_PDF);
  }

  /**
   * @method
   * @description
   * it exposes all the allowed command manager interface provided by preview APIs
   *
   * @return {Promise} - promise that resolves to command manager object
   */
  getCommandManager() {
    return this.executeCommand(_APICommands.PreviewAPICommands.GET_COMMAND_MANAGER);
  }

  /**
   * @method
   * @description
   * it registers a callback for 'before RHP Opens' with preview APIs
   */
  registerBeforeRHPOpenCallback(callbackFn, opts) {
    return this.executeCommand(_APICommands.PreviewAPICommands.REGISTER_BEFORE_RHP_OPEN_CALLBACK, {
      callbackFn,
      opts
    });
  }

  /**
   * @method
   * @description
   * it registers callback for 'before quickTools gets activated' with preview APIs
   */
  registerBeforeQuickToolActivatedCallback(callbackFn, opts) {
    return this.executeCommand(_APICommands.PreviewAPICommands.REGISTER_BEFORE_QUICK_TOOL_ACTIVATED_CALLBACK, {
      callbackFn,
      opts
    });
  }
  registerBeforeStickyToolActivatedCallback(callbackFn, opts) {
    return this.executeCommand(_APICommands.PreviewAPICommands.REGISTER_BEFORE_STICKY_TOOL_ACTIVATED_CALLBACK, {
      callbackFn,
      opts
    });
  }

  /**
   * @method
   * @description
   * show coachMark for a verbId in preview dropin
   *
   * @param {String} dropinId - Id of the dropin for which coachMark will be shown
   */
  showCoachMark(dropinId) {
    return this.executeCommand(_APICommands.PreviewAPICommands.SHOW_COACH_MARK, dropinId);
  }

  /**
   * @method
   * @description
   * resets the quick tool to default state.
   */
  resetQuickTool(options) {
    return this.executeCommand(_APICommands.PreviewAPICommands.RESET_QUICK_TOOL, options);
  }

  /**
   * @method
   * @description
   * subscribes to quick tool on verb change
   */
  subscribeQuickTool(callback) {
    return this.executeCommand(_APICommands.PreviewAPICommands.SUBSCRIBE_QUICK_TOOL, callback);
  }

  /**
   * @method
   * @description
   * returns the currently activated quick tool
   */
  getActiveQuickTool() {
    return this.executeCommand(_APICommands.PreviewAPICommands.GET_ACTIVE_QUICK_TOOL);
  }

  /**
   * @method
   * @description
   * show/hide the quick tool panel.
   *
   * @param {Boolean} visible QuickTool Panel is visible if param is true
   */
  showQuickTool(visible) {
    return this.executeCommand(_APICommands.PreviewAPICommands.SHOW_QUICK_TOOL, visible);
  }

  /**
   * @method
   * @description
   * show/hide full screen button in RHP.
   *
   * @param {Boolean} visible - Full Screen Button is visible in RHP if param is true
   */
  enableFullScreen(visible) {
    return this.executeCommand(_APICommands.PreviewAPICommands.FULL_SCREEN_BUTTON_VISIBILITY, visible);
  }

  /**
   * @method
   * @description
   * Function to disable quick tool verbs by invoking the Preview API command
   * 'DISABLE_QUICK_TOOL_VERBS' with given configuration.
   * @param {object} config - An object containing configuration parameters.
   * @returns {*} Result of the executed command, returned by 'executeCommand' method of the instance.
   */
  disableQuickToolVerbs(config) {
    return this.executeCommand(_APICommands.PreviewAPICommands.DISABLE_QUICK_TOOL_VERBS, config);
  }

  /**
   * @method
   * @description
   * it registers callback for 'before navigation bar tool activated' with preview APIs
   */
  registerBeforeNavigationBarToolActivatedCallback(callbackFn, opts) {
    return this.executeCommand(_APICommands.PreviewAPICommands.REGISTER_BEFORE_NAVIGATION_BAR_TOOL_ACTIVATED_CALLBACK, {
      callbackFn,
      opts
    });
  }

  /**
   * @method
   * @description
   * show/hide the commenting tools.
   *
   * @param {Boolean} visible Commenting Tools are visible if param is true
   */
  forceShowCommentingTools(visible) {
    return this.executeCommand(_APICommands.PreviewAPICommands.FORCE_SHOW_COMMENTING_TOOLS, visible);
  }

  /**
   * @method
   * @description
   * check if editing is allowed on a document
   */
  isEditingAllowed() {
    return this.executeCommand(_APICommands.PreviewAPICommands.IS_EDITING_ALLOWED);
  }
  isCommentingAllowed() {
    return this.executeCommand(_APICommands.PreviewAPICommands.IS_COMMENTING_ALLOWED);
  }
  updateDocInfo(data) {
    return this.executeCommand(_APICommands.PreviewAPICommands.UPDATE_DOC_INFO, data);
  }

  /**
   * @method
   * @description
   * To update modifed filename in viewer into preview document stores.
   *
   * @param {string} fileName Updated FileName
   */
  setFileName(fileName) {
    this.executeCommand(_APICommands.PreviewAPICommands.SET_FILE_NAME, fileName);
  }

  /**
   * @method
   * @description
   * To update modifed file metadata in viewer into preview document stores.
   *
   * @param {object} fileMetadata Updated file metadata
   */
  updateFileMetadata(fileMetadata) {
    this.executeCommand(_APICommands.PreviewAPICommands.UPDATE_FILE_META_DATA, fileMetadata);
  }

  /**
   * @method
   * @description
   * expands RHP embedded with the tool whose ID is passed as argument
   */
  openRHPPanel(toolId, opts) {
    return this.executeCommand(_APICommands.PreviewAPICommands.OPEN_RHP_PANEL, toolId, opts);
  }

  /**
   * @method
   * @description
   * closes the quick tool to default state
   */
  closeRHPPanel() {
    return this.executeCommand(_APICommands.PreviewAPICommands.CLOSE_RHP_PANEL);
  }

  /**
   * @method
   * @description
   * subscribes to RHP Dropin Switch event
   */
  subscribeToRHP(callback) {
    return this.executeCommand(_APICommands.PreviewAPICommands.SUBSCRIBE_TO_RHP, callback);
  }

  /**
   * @method
   * @description
   * unsubscribes from RHP Dropin Switch event
   */
  unsubscribeFromRHP(callback) {
    return this.executeCommand(_APICommands.PreviewAPICommands.UNSUBSCRIBE_FROM_RHP, callback);
  }

  /**
   * @method
   * @description
   * returns the currently expanded rhp tool's config
   */
  getRHPConfig() {
    return this.executeCommand(_APICommands.PreviewAPICommands.GET_RHP_CONFIG);
  }

  /**
   * @method
   * @description
   * register verbs to be shown in RHP panel
   */
  addRHPUIControl(options) {
    return this.executeCommand(_APICommands.PreviewAPICommands.ADD_RHP_UI_CONTROL, options);
  }

  /**
   * @method
   * @description
   * method to update preview app config
   *
   * @param {String} newAppConfig - Stringified app config
   */
  updatePreviewAppConfig(newAppConfig) {
    return this.executeCommand(_APICommands.PreviewAPICommands.UPDATE_PREVIEW_APP_CONFIG, newAppConfig);
  }

  /**
   * @method
   * @description
   * Method to switch to CNPDF only after ajsMetadata is successfully fetched via PDF
   *
   * @param {Object} cnpdfFetcher - CNPDF fetcher object to fetch the cnpdf
   */
  switchToCNPDF(cnpdfFetcher) {
    return this.executeCommand(_APICommands.PreviewAPICommands.SWITCH_TO_CNPDF, cnpdfFetcher);
  }

  /**
   * @method
   * @description
   * Method to notify asset version update to preview app. It is currently called from saveFile.js
   * @param {Object} event - Event object
   * @return {any} - Returns the response from the notifyAssetVersionUpdate method
   */
  notifyAssetVersionUpdate(event = {}) {
    return this.executeCommand(_APICommands.PreviewAPICommands.NOTIFY_ASSET_VERSION_UPDATE, event);
  }

  /**
   * @method
   * @description
   * Method to resolve conflicts in the document. It expects an object containing a comments object
   * which contains diff comments to be resolved.
   * @param {Object} params - Contains a comments object which contains diff comments to be resolved.
   * @param {Object} params.comments - The comments object.
   * @param {Array} params.comments.add - Array of comments to be added.
   * @param {Array} params.comments.delete - Array of comments to be deleted.
   * @param {Array} params.comments.update - Array of comments to be updated.
   * @returns {Promise} - Promise that resolves once the conflict resolution is done.
   */
  resolveConflicts(params) {
    return new Promise((resolve, reject) => {
      const editApis = _ServiceRegistry.default.getService("EditApi").getExposedAPIs();
      if (editApis && editApis.resolveConflicts) {
        editApis.resolveConflicts(params).then(data => resolve(data)).catch(error => reject(error));
      } else {
        reject("resolveConflicts API not found");
      }
    });
  }

  /**
   * @method
   * @description
   * Shows a dialog for conflicting file
   * @param {Object} params - Contains user information for the conflicting file
   * @param {String} params.mail - Email address of the user with conflicting changes
   * @param {String} params.name - Name of the user with conflicting changes
   * @return {Promise} - Promise that resolves when the conflicting file dialog is shown
   */
  showConflictingFileDialog(params) {
    return this.executeCommand(_APICommands.PreviewAPICommands.SHOW_CONFLICTING_FILE_DIALOG, params);
  }

  /**
   * @method
   * @description
   * check if document is protected or not (password protected or encrypted)
   */
  isFileProtected() {
    return this.executeCommand(_APICommands.PreviewAPICommands.IS_FILE_PROTECTED);
  }

  /**
   * @method
   * @description
   * it provides all the permissions and security information related to document
   *
   *
   * @return {Promise} return Promise resolved with all document security information
   */
  getPDFPermissions() {
    return this.executeCommand(_APICommands.PreviewAPICommands.GET_PDF_PERMISSIONS);
  }
  getNotificationApis() {
    return {
      /**
       * @method
       * @description
       * Show notification (toast or banner)  using preview api
       *
       * @param {String} message - Message to show in the notification
       * @param {Object} options - Optional parameters to pass for notification
       */
      // eslint-disable-next-line max-len
      showNotification: (message, options) => this.executeCommand(_APICommands.PreviewAPICommands.SHOW_NOTIFICATION, message, options),
      /**
       * @method
       * @description
       * Clear all notifications (both toasts and banners)
       */
      clearAllNotifications: () => this.executeCommand(_APICommands.PreviewAPICommands.CLEAR_ALL_NOTIFICATIONS),
      /**
       * @method
       * @description
       * Clear notification by id
       * @param {String} notificationId - Id of the notification to clear
       * */
      clearNotification: notificationId => this.executeCommand(_APICommands.PreviewAPICommands.CLEAR_NOTIFICATION, notificationId)
    };
  }

  /**
   * @method
   * @description
   * to get the thumbnail buffer of a particular page
   * @param {Integer} pageNum | 1 index based page number
   * @param {Object} thumbnailRect | An object containing the thumbnail rect (height and width)
   * @returns a promise which resolves to an object containing thumbnail array buffer and mimeType
   */

  getPageThumbnail(pageNum, thumbnailRect) {
    return this.executeCommand(_APICommands.PreviewAPICommands.GET_PAGE_THUMBNAIL, {
      pageNum,
      thumbnailRect
    });
  }

  /**
   * @method
   * @description
   * open the document in edit mode
   */
  enterEditMode() {
    return this.executeCommand(_APICommands.AppAPICommands.ENTER_EDIT_MODE);
  }

  /**
   * @method
   * @description
   * change the prop dynamically to load edit module
   */
  updateEditEnabled(editEnabled, toolsBoardEnabled = false) {
    return this.executeCommand(_APICommands.AppAPICommands.UPDATE_EDIT_ENABLED, {
      editEnabled,
      toolsBoardEnabled
    });
  }

  /**
   * @method
   * @description
   * close edit mode
   */
  closeEditMode() {
    return this.executeCommand(_APICommands.AppAPICommands.CLOSE_EDIT_MODE);
  }

  /**
   * @method
   * @description
   * this API returns the current view mode
   * @return {Promise} - a promise that resolves with current viewMode.
   */
  getViewMode() {
    return this.executeCommand(_APICommands.PreviewAPICommands.GET_VIEW_MODE);
  }

  /**
   * @method
   * @description
   * Exit from full screen mode
   * @return {Promise} - a promise that resolves with true if PDF
   * was in full screen mode, otherwise, resolves with false.
   */
  exitFullScreenMode() {
    return this.executeCommand(_APICommands.PreviewAPICommands.EXIT_FULL_SCREEN_MODE);
  }

  /**
   * @method
   * @description
   * this API return the toolsboard config
   *  @param {String} type of config
   */
  getToolsboardConfig(type) {
    return this.executeCommand(_APICommands.AppAPICommands.GET_TOOLSBOARD_CONFIG, type);
  }

  /**
   * @method
   * @description
   * check if PDF JavaScript is enabled or not.
   *
   * @return {Promise} - a promise that resolves with true if PDF
   * JavaScript is enabled, otherwise, resolves with false.
   */
  isPDFJavaScriptEnabled() {
    return this.executeCommand(_APICommands.PreviewAPICommands.IS_PDF_JAVASCRIPT_ENABLED);
  }

  /**
   * @method
   * @description
   * execute Doc/WillClose actions
   */
  executeDocWillCloseActions() {
    return this.executeCommand(_APICommands.PreviewAPICommands.EXECUTE_DOC_WILL_CLOSE_ACTIONS);
  }

  /**
   * @method
   * @description
   * @param fileInfo fileInfo of copied asset.
   * API to retarget Viewer and Preview to the newly created copied asset.
   */

  setViewerRetargetedFileInfo(fileInfo) {
    return this.executeCommand(_APICommands.PreviewAPICommands.SET_VIEWER_RETARGETED_FILE_INFO, fileInfo);
  }

  /**
   * @method
   * @description
   * enable or disable the PDF JavaScript feature
   *
   * @param {Boolean} enable PDF JavaScript enabled if param is true.
   */
  enablePDFJavaScript(enable) {
    return this.executeCommand(_APICommands.PreviewAPICommands.ENABLE_PDF_JAVASCRIPT, enable);
  }

  /**
   * @method
   * @description
   * API to Enable or Disable Acroform Validation and Formatting. This is in effect if PDF JavaScript is disabled
   * @param {Boolean} shouldEnable
   */

  enableAcroformValidationFormatting(shouldEnable) {
    return this.executeCommand(_APICommands.PreviewAPICommands.ENABLE_ACROFORM_VALIDATION_FORMATTING, shouldEnable);
  }

  /**
   * @method
   * @description
   * enable or disable the Overprint support for page rendition
   *
   * @param {Boolean} enable true/false to enable/disable overprint support
   */
  enableOverprintSupport(enable) {
    return this.executeCommand(_APICommands.PreviewAPICommands.ENABLE_OVERPRINT_SUPPORT, enable);
  }

  /**
   * @method
   * @description
   * set the selected asset
   *
   * @param {Object|Promise<Object>} selectedAsset either an object or
   * a promise that resolves with an object representing the selected asset.
   */
  setSelectedAsset(selectedAsset) {
    return this.executeCommand(_APICommands.PreviewAPICommands.SET_SELECTED_ASSET, selectedAsset);
  }

  /**
   * @method
   * @description
   * API to set view mode
   * @param {String} viewMode
   */
  setViewMode(viewMode) {
    return this.executeCommand(_APICommands.PreviewAPICommands.SET_VIEW_MODE, viewMode);
  }

  /**
   * @method
   * @description
   * API to add page numbers to the pdf
   * @param startPageNum pageNo from where the numbering has to start
   * @param endPageNum pageNo from where the page numbering has to end
   * @param startPageNumber start number for page numbering
   * @param {Object} fontStyle {
          fontColor: [0, 0, 0, 1], color of the page number
          fontName: "MyriadPro-Regular", psName of the font of the page number
          fontSize: 8, size of the page number
          isUnderline: false whether page number is to be underlined or not
      }
   * @param {Object}position postion of the page number
      {
          hAlign: 2, page number horizontal alignment.possible values are 0, 1, 2
          vAlign: 2 , page number vertical alignment.possible values are 0, 1
      }
  * @returns
   */
  addPageNumbers({
    startPageNum,
    endPageNum,
    startPageNumber,
    fontStyle,
    position
  } = {}) {
    return new Promise((resolve, reject) => {
      const editApis = _ServiceRegistry.default.getService("EditApi").getExposedAPIs();
      if (editApis.addPageNumbers) {
        editApis.addPageNumbers({
          startPageNum,
          endPageNum,
          startPageNumber,
          fontStyle,
          position
        }).then(data => resolve(data)).catch(error => reject(error));
      } else {
        reject("Edit APIs not enabled.");
      }
    });
  }

  /**
   * @method
   * @description
   * API to add items to contextMenu
   * @param {Function} callback A function that returns a promise resolved to an array of menu item objects.
   * @param {String} triggerType The trigger type of the context menu to subscribe to.
   * Possible values are "rightClick" and "textSelection". (Default: "rightClick")
   */
  registerContextMenuHandler(callback, triggerType) {
    return this.executeCommand(_APICommands.PreviewAPICommands.REGISTER_CONTEXT_MENU_HANDLER, {
      callback,
      triggerType
    });
  }

  /**
   * @method
   * @description subscribe to PDF edit operations
   * @param {Function} callback - callback function that will be called on edit operations.
   * callback will receive the event name and params as arguments
   * @return None
   */
  subscribeToPDFEditOperations(callback) {
    this.executeCommand(_APICommands.PreviewAPICommands.SUBSCRIBE_TO_PDF_EDIT_OPERATIONS, callback);
  }

  /**
   * @method
   * @description Get the updated PDF buffer
   * @param {Boolean} incremental - default is false, true if incremental update is required
   * @returns {Promise} promise that resolves with updated PDF buffer
   */
  getUpdatedPDFBuffer(incremental = false) {
    return this.executeCommand(_APICommands.PreviewAPICommands.GET_UPDATED_PDF_BUFFER, incremental);
  }
  setAssetInfo(assetInfo) {
    this.executeCommand(_APICommands.PreviewAPICommands.SET_ASSET_INFO, assetInfo);
  }

  /**
   * Returns an object containing document overlay APIs.
   * @returns {Object} The document overlay APIs object.
   */
  getPageOverlayApis() {
    return {
      /**
       * Registers a page overlay for a specific client.
       *
       * @param {string} clientName - The name of the client.
       * @param {React.Component} overlayComponent - The overlay component to be registered.
       * @param {Object} api - The API object to be passed to the overlay component.
       * @param {Object} options - The additional options object to be passed to the overlay component like styles
       */
      registerOverlay: (clientName, overlayComponent, api, options) => this.executeCommand(_APICommands.PreviewAPICommands.REGISTER_OVERLAY, {
        clientName,
        overlayComponent,
        api,
        options
      }),
      /**
       * Shows an overlay for the specified client.
       *
       * @param {string} clientName - The name of the client.
       */
      showOverlay: clientName => this.executeCommand(_APICommands.PreviewAPICommands.SHOW_OVERLAY, clientName),
      /**
       * Hides the overlay for a specific client.
       *
       * @param {string} clientName - The name of the client.
       */
      hideOverlay: clientName => this.executeCommand(_APICommands.PreviewAPICommands.HIDE_OVERLAY, clientName)
    };
  }

  /**
   * Returns the page view APIs.
   * @returns {Object} The page view APIs object.
   */
  getPageViewApis() {
    return {
      /**
       * Transforms coordinates of a point from view space to PDF space.
       *
       * @param {number} x - The x-coordinate.
       * @param {number} y - The y-coordinate.
       * @param {number} pageNum - The page number.
       * @returns {Promise} - A promise that resolves with PDF space coordinates when the command is executed.
       */
      transformViewToPDF: (x, y, pageNum) => this.executeCommand(_APICommands.PreviewAPICommands.TRANSFORM_VIEW_TO_PDF, {
        x,
        y,
        pageNum
      }),
      /**
       * Transforms coordinates of a point from PDF space to view space.
       *
       * @param {number} x - The x-coordinate.
       * @param {number} y - The y-coordinate.
       * @param {number} pageNum - The page number.
       * @returns {Promise} - A promise that resolves with view space coordinates when the command is executed.
       */
      transformPDFToView: (x, y, pageNum) => this.executeCommand(_APICommands.PreviewAPICommands.TRANSFORM_PDF_TO_VIEW, {
        x,
        y,
        pageNum
      }),
      /**
       * Returns the visible page rect in PDF space.
       * @param {number} pageNumber - The page number.
       * @returns {Promise} - A promise that resolves with the viewport of the page when the command is executed
       */
      getVisiblePagePDFRect: pageNumber => this.executeCommand(_APICommands.PreviewAPICommands.GET_VISIBLE_PAGE_PDF_RECT, pageNumber)
    };
  }
  getEditAPIs() {
    return {
      preloadEdit: opts => this.executeCommand(_APICommands.PreviewAPICommands.PRELOAD_EDIT, opts),
      insertText: (text, clickPoint, pageIndex, opts) => this.executeCommand(_APICommands.PreviewAPICommands.INSERT_TEXT, {
        text,
        clickPoint,
        pageIndex,
        opts
      }),
      replaceText: (text, quads, pageIndex, opts) => this.executeCommand(_APICommands.PreviewAPICommands.REPLACE_TEXT, {
        text,
        quads,
        pageIndex,
        opts
      }),
      deleteText: (quads, pageIndex, opts) => this.executeCommand(_APICommands.PreviewAPICommands.DELETE_TEXT, {
        quads,
        pageIndex,
        opts
      }),
      discardPreview: () => this.executeCommand(_APICommands.PreviewAPICommands.DISCARD_PREVIEW)
    };
  }
  getBottomBoardApis() {
    return {
      /**
       * sets dropinId to be loaded from preview
       * @param {str} dropinId
       * @param {Object} props
       * @returns {Void}
       */
      setPanel: (dropinId, props = {}) => this.executeCommand(_APICommands.PreviewAPICommands.SET_BOTTOM_BOARD_PANEL, {
        dropinId,
        props
      }),
      /**
       * show/hide bottom board panel
       * @param {boolean} val
       * @param {boolean} overrideVisibility
       * @returns {Void}
       */
      showPanel: (val, overrideVisibility = false) => this.executeCommand(_APICommands.PreviewAPICommands.SHOW_BOTTOM_BOARD_PANEL, {
        val,
        overrideVisibility
      }),
      /**
       * sets banner component to be added at the end of document
       * @param {Element} banner
       * @returns {Void}
       */
      setBanner: banner => this.executeCommand(_APICommands.PreviewAPICommands.SET_BOTTOM_BOARD_BANNER, banner),
      /**
       * show/hide bottom board banner
       * @param {boolean} val
       * @returns {Void}
       */
      showBanner: val => this.executeCommand(_APICommands.PreviewAPICommands.SHOW_BOTTOM_BOARD_BANNER, val),
      /**
       * provides ability to customize apperance and have default handlers enabled
       * @param {Object} config
       * @returns {Void}
       */
      setConfig: config => this.executeCommand(_APICommands.PreviewAPICommands.SET_BOTTOM_BOARD_CONFIG, config)
    };
  }
  getFabApis() {
    return {
      /**
       * sets verbId to be loaded from preview
       * @param {str} verbId
       * @param {object} opts
       * @returns {Void}
       */
      setFabVerbId: (verbId, opts) => this.executeCommand(_APICommands.PreviewAPICommands.SET_FAB_VERB_ID, {
        verbId,
        opts
      }),
      /**
       * show/hide FAB
       * @param {boolean} val
       * @returns {Void}
       */
      setFabVisibility: val => this.executeCommand(_APICommands.PreviewAPICommands.SET_FAB_VISIBILITY, val)
    };
  }
  getFloatingWindowsApis() {
    return {
      /**
       * @description
       * adds a floating window to the preview
       * @param {Object} params : (required) {
              id?: string (optional);
              targetRect: (optional) {
                  x: number,
                  y: number,
                  width: number,
                  height: number,
                  positionPriority: (optional) ["top","left","bottom","right"]
              };
              targetPoint: (optional) {
                  x: number | "left" | "center" | "right",
                  y: number | "top" | "center" | "bottom"
              };
              isDraggable: boolean;
              component?: ReactNode;
              margin?: number;
          }
          @returns {Promise<FloatingWindowHandlingApis>} - promise that resolves with FloatingWindowHandlingApis:
              - getWindowId(): string;
              - getDraggableState(): boolean;
              - getTargetRect(): {x: number, y: number, width: number, height: number} | null;
              - getTargetPoint(): {
                  x: number | "left" | "center" | "right",
                  y: number | "top" | "center" | "bottom"
              } | null;
              - getComponent(): ReactNode;
              - getMargin(): number;
              - toggleDraggableState(callback: (state=>boolean)): void;
              - toggleCloseBtn(callback:  (state=>boolean)): void;
              - updateFloatingWindowContent(component: ReactNode): void;
              - on(event: EventType, listener: EventListener): void;
              - removeListener(event: EventType, listener?: EventListener): void;
              - emit(event: EventType, ...args: any[]): void;
              - show(): void;
              - hide(): void;
              - moveTo(x: number, y: number): void;
              - drag(): void;
              - triggerAnimation(animationType: "shake" | ...future..animations...): void;
              - overrideClassName(newClassName: string): void;
              - overrideCSS(newCSS: React.CSSProperties): void;
       * */
      addWindow: params => this.executeCommand(_APICommands.PreviewAPICommands.ADD_FLOATING_WINDOW, params)
    };
  }
  getTopPanelApis() {
    return {
      /**
       * sets verbId to be loaded from preview
       * @param {str} verbId
       * @param {object} opts
       * @returns {Void}
       */
      setTopPanelVerbId: (verbId, opts) => this.executeCommand(_APICommands.PreviewAPICommands.SET_TOP_PANEL_VERB_ID, {
        verbId,
        opts
      }),
      /**
       * show/hide Top Panel
       * @param {boolean} val
       * @returns {Void}
       */
      setTopPanelVisibility: val => this.executeCommand(_APICommands.PreviewAPICommands.SET_TOP_PANEL_VISIBILITY, val),
      /**
       * Expand/Collapse Top Panel
       * @param {boolean} val
       * @returns {Void}
       */
      setTopPanelExpandedState: val => this.executeCommand(_APICommands.PreviewAPICommands.SET_TOP_PANEL_EXPANDED_STATE, val)
    };
  }
  getExposedAPIs(options) {
    const publicExposedApis = {
      getPDFMetadata: this.getPDFMetadata.bind(this),
      getXMPMetadata: this.getXMPMetadata.bind(this),
      getSelectedContent: this.getSelectedContent.bind(this),
      resetCursor: this.resetCursor.bind(this),
      getCurrentPage: this.getCurrentPage.bind(this),
      getPageZoom: this.getPageZoom.bind(this),
      setCursor: this.setCursor.bind(this),
      gotoLocation: this.gotoLocation.bind(this),
      addBottomSpaceBelowDocumentInMobile: this.addBottomSpaceBelowDocumentInMobile.bind(this),
      enableTextSelection: this.enableTextSelection.bind(this),
      enableInteractiveTooltip: this.enableInteractiveTooltip.bind(this),
      clearPageSelection: this.clearPageSelection.bind(this),
      search: searchText => {
        if (options.previewConfig.enableSearchAPIs) {
          return this.search(searchText);
        }
        return Promise.reject("Search APIs not enabled.");
      },
      performSearch: (text, startPage, numPages, priority, zoomLevel) => this.performSearch(text, startPage, numPages, priority, zoomLevel),
      getZoomAPIs: this.getZoomAPIs.bind(this),
      getBookmarkAPIs: this.getBookmarkAPIs.bind(this),
      getAttachmentAPIs: this.getAttachmentAPIs.bind(this)
    };
    if (this._isViewSDKPublic) {
      return publicExposedApis;
    }
    return _objectSpread({
      registerContextMenuHandler: this.registerContextMenuHandler.bind(this),
      getTextFromDocRects: this.getTextFromDocRects.bind(this),
      getCaretModeApis: this.getCaretModeApis.bind(this),
      getSearchUI: this.getSearchUI.bind(this),
      registerSearchAddOnPanel: this.registerSearchAddOnPanel.bind(this),
      registerStickyToolsAddOnPanel: this.registerStickyToolsAddOnPanel.bind(this),
      closeViewer: this.closeViewer.bind(this),
      unMountViewer: this.unMountViewer.bind(this),
      getReadAloudAPIs: this.getReadAloudAPIs.bind(this),
      getThumbnail: this.getThumbnail.bind(this),
      savePDF: this.savePDF.bind(this),
      applyCNPDFUpdates: this.applyCNPDFUpdates.bind(this),
      resolveConflicts: this.resolveConflicts.bind(this),
      showConflictingFileDialog: this.showConflictingFileDialog.bind(this),
      downloadPDF: this.downloadPDF.bind(this),
      getCommandManager: this.getCommandManager.bind(this),
      registerBeforeRHPOpenCallback: this.registerBeforeRHPOpenCallback.bind(this),
      registerBeforeQuickToolActivatedCallback: this.registerBeforeQuickToolActivatedCallback.bind(this),
      registerBeforeStickyToolActivatedCallback: this.registerBeforeStickyToolActivatedCallback.bind(this),
      showCoachMark: this.showCoachMark.bind(this),
      resetQuickTool: this.resetQuickTool.bind(this),
      subscribeQuickTool: this.subscribeQuickTool.bind(this),
      getActiveQuickTool: this.getActiveQuickTool.bind(this),
      showQuickTool: this.showQuickTool.bind(this),
      enableFullScreen: this.enableFullScreen.bind(this),
      disableQuickToolVerbs: this.disableQuickToolVerbs.bind(this),
      // eslint-disable-next-line max-len
      registerBeforeNavigationBarToolActivatedCallback: this.registerBeforeNavigationBarToolActivatedCallback.bind(this),
      isEditingAllowed: this.isEditingAllowed.bind(this),
      isCommentingAllowed: this.isCommentingAllowed.bind(this),
      setFileName: this.setFileName.bind(this),
      updateFileMetadata: this.updateFileMetadata.bind(this),
      openRHPPanel: this.openRHPPanel.bind(this),
      closeRHPPanel: this.closeRHPPanel.bind(this),
      subscribeToRHP: this.subscribeToRHP.bind(this),
      unsubscribeFromRHP: this.unsubscribeFromRHP.bind(this),
      getRHPConfig: this.getRHPConfig.bind(this),
      addRHPUIControl: this.addRHPUIControl.bind(this),
      updatePreviewAppConfig: this.updatePreviewAppConfig.bind(this),
      switchToCNPDF: this.switchToCNPDF.bind(this),
      forceShowCommentingTools: this.forceShowCommentingTools.bind(this),
      isFileProtected: this.isFileProtected.bind(this),
      getNotificationApis: this.getNotificationApis.bind(this),
      getPageThumbnail: this.getPageThumbnail.bind(this),
      enterEditMode: this.enterEditMode.bind(this),
      closeEditMode: this.closeEditMode.bind(this),
      getToolsboardConfig: this.getToolsboardConfig.bind(this),
      isPDFJavaScriptEnabled: this.isPDFJavaScriptEnabled.bind(this),
      executeDocWillCloseActions: this.executeDocWillCloseActions.bind(this),
      addPageNumbers: this.addPageNumbers.bind(this),
      updateEditEnabled: this.updateEditEnabled.bind(this),
      setViewerRetargetedFileInfo: this.setViewerRetargetedFileInfo.bind(this),
      enablePDFJavaScript: this.enablePDFJavaScript.bind(this),
      enableAcroformValidationFormatting: this.enableAcroformValidationFormatting.bind(this),
      enableOverprintSupport: this.enableOverprintSupport.bind(this),
      setViewMode: this.setViewMode.bind(this),
      exitFullScreenMode: this.exitFullScreenMode.bind(this),
      getViewMode: this.getViewMode.bind(this),
      switchPreview: this.switchPreview.bind(this),
      setDarkPageThemeSwitchDisabled: this.setDarkPageThemeSwitchDisabled.bind(this),
      setDocumentViewThemeChange: this.setDocumentViewThemeChange.bind(this),
      subscribeToPDFEditOperations: this.subscribeToPDFEditOperations.bind(this),
      getUpdatedPDFBuffer: this.getUpdatedPDFBuffer.bind(this),
      setAssetInfo: this.setAssetInfo.bind(this),
      setSelectedAsset: this.setSelectedAsset.bind(this),
      getPDFPermissions: this.getPDFPermissions.bind(this),
      getDocumentId: this.getDocumentId.bind(this),
      getInitialPageLoaded: this.getInitialPageLoaded.bind(this),
      getPageOverlayApis: this.getPageOverlayApis.bind(this),
      getPageViewApis: this.getPageViewApis.bind(this),
      getPageAttributes: this.getPageAttributes.bind(this),
      extractPageText: this.extractPageText.bind(this),
      registerBeforeA11yTreeRenderCallback: this.registerBeforeA11yTreeRenderCallback.bind(this),
      getEditAPIs: this.getEditAPIs.bind(this),
      bottomBoardApis: this.getBottomBoardApis.bind(this),
      fabApis: this.getFabApis.bind(this),
      topPanelApis: this.getTopPanelApis.bind(this),
      getBBoxForPage: this.getBBoxForPage.bind(this),
      getBBoxContent: this.getBBoxContent.bind(this),
      getTextBetweenPDFPoints: this.getTextBetweenPDFPoints.bind(this),
      getFloatingWindowsApis: this.getFloatingWindowsApis.bind(this),
      updateDocInfo: this.updateDocInfo.bind(this)
    }, publicExposedApis);
  }
}
exports.default = CommandManager;